import { useTranslation } from "react-i18next";
import React, { useState, useRef, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FormGroup, FormFeedback, Input, Button, Card, CardBody, CardTitle } from 'reactstrap';
import { setQRCard, deleteQRCode } from 'store/slices/personalSlice';
import Loading from 'Components/Loading';
import { extractErrorMessage } from 'Modules/ParseAPI';
import { ModernAPI } from 'Modules/ModernAPI';
import './QRCard.scss';

const QRCard = ({
  item,
  cards,
  setQRCard,
  deleteQRCode
}) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editLabel, setEditLabel] = useState(item.label);
  const inputEl = useRef(null);
  const labelInputEl = useRef(null);
  useEffect(() => {
    if (inputEl.current && showForm) {
      inputEl.current.focus();
    }
  }, [showForm]);
  useEffect(() => {
    if (labelInputEl.current && isEditingLabel) {
      labelInputEl.current.focus();
      labelInputEl.current.select();
    }
  }, [isEditingLabel]);
  useEffect(() => {
    setEditLabel(item.label);
  }, [item.label]);

  const onChange = e => {
    setError(null);
    setLoading(true);
    setQRCard(e.target.value, item.id).catch(e => setError(extractErrorMessage(e))).finally(() => {
      setLoading(false);
      setShowForm(false);
    });
  };

  const handleLabelEdit = () => {
    setIsEditingLabel(true);
  };

  const handleLabelSave = async () => {
    if (editLabel.trim() === item.label) {
      setIsEditingLabel(false);
      return;
    }

    if (!editLabel.trim()) {
      setError('Label cannot be empty');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const updatedQRCode = await ModernAPI.updateQRCodeLabel(item.id, editLabel.trim()); // Update Redux state with the updated QR code

      dispatch({
        type: 'personal/qrCodeUpdate',
        payload: {
          qrCode: updatedQRCode
        }
      });
      setIsEditingLabel(false);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleLabelCancel = () => {
    setEditLabel(item.label);
    setIsEditingLabel(false);
    setError(null);
  };

  const handleLabelKeyPress = e => {
    if (e.key === 'Enter') {
      handleLabelSave();
    } else if (e.key === 'Escape') {
      handleLabelCancel();
    }
  };

  const qrIdentifier = item.code || item.id;
  const qrDisplayLabel = item.label || item.name || t('Card QR Code');

  const handleDownload = () => {
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://hmiqr.com/qr/${qrIdentifier}`; // Create a temporary anchor element to trigger download

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${qrDisplayLabel}.png`;
    link.target = '_blank'; // Append to body, click, and remove

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this QR code?')) {
      setLoading(true);

      try {
        await deleteQRCode(item.id);
      } catch (e) {
        setError(extractErrorMessage(e));
      } finally {
        setLoading(false);
      }
    }
  };

  const isDefault = item.isDefault === true;

  if (showForm && !isDefault) {
    return <Card className="qr-card">
        <CardBody>
          {loading && <Loading />}
          <div className="w-100">
            <h6 className="fw-bold mb-3">{t("Change Associated Card")}</h6>
            <FormGroup>
              <label className="small text-muted mb-1">{t("Select Card:")}</label>
              <Input type='select' name='property' value={item.cardId || ''} onChange={onChange} innerRef={inputEl} invalid={!!error}>
                {(cards || []).filter(c => (c.name || '').toLowerCase() !== 'public').map(card => <option key={card.id || card.objectId} value={card.id || card.objectId}>
                    {card.name ? t(card.name) : t('Card')}
                  </option>)}
              </Input>
              <FormFeedback>{error}</FormFeedback>
            </FormGroup>
            <div className="d-flex justify-content-end mt-3">
              <Button color="secondary" size="sm" onClick={() => setShowForm(false)}>{t("Cancel")}</Button>
            </div>
          </div>
        </CardBody>
      </Card>;
  }

  return <Card className="qr-card">
      <CardBody>
        {
        /* Header / Label */
      }
        <div className="qr-card-header">
          {isDefault && <div className="qr-card-badge">{t("Primary Card")}</div>}
          {isDefault ? (
            <CardTitle tag="h6" className="qr-card-title" title={qrDisplayLabel}>
              {qrDisplayLabel}
            </CardTitle>
          ) : isEditingLabel ? (
            <div className="d-flex align-items-center justify-content-center gap-1">
              <Input type="text" value={editLabel} onChange={e => setEditLabel(e.target.value)} onKeyDown={handleLabelKeyPress} onBlur={e => {
                if (!e.relatedTarget || !e.relatedTarget.closest('button')) {
                  handleLabelSave();
                }
              }} innerRef={labelInputEl} className="text-center py-1 px-2" style={{
                maxWidth: '180px',
                fontSize: '0.9rem'
              }} invalid={!!error} />
              <Button color="success" size="sm" onClick={handleLabelSave} className="px-2 py-1" disabled={loading}>
                <span role="img" aria-label="save">✓</span>
              </Button>
              <Button color="secondary" size="sm" onClick={handleLabelCancel} className="px-2 py-1" disabled={loading}>
                <span role="img" aria-label="cancel">✕</span>
              </Button>
            </div>
          ) : (
            <CardTitle tag="h6" className="qr-card-title" style={{
              cursor: 'pointer'
            }} onClick={handleLabelEdit} title={t("Click to edit label")}>
              {qrDisplayLabel} <span role="img" aria-label="edit" style={{ fontSize: '0.8rem', opacity: 0.7 }}>✏️</span>
            </CardTitle>
          )}
        </div>

        {
        /* QR Code Display */
      }
        <div className="qr-code-img-wrapper">
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=https://hmiqr.com/qr/${qrIdentifier}`} alt={`QR Code for ${qrDisplayLabel}`} />
        </div>

        {
        /* QR Code ID */
      }
        <div className="qr-code-id-display" title={qrIdentifier}>
          {qrIdentifier}
        </div>

        {
        /* Card Assignment */
      }
        <div className="qr-card-target-row">
          <span className="qr-target-label">{t("Points to card:")}</span>
          {isDefault ? (
            <span className="qr-target-val" title={item.cardName || item.cardname || t('Default Card')}>
              {item.cardName || item.cardname || t('Default Card')}
            </span>
          ) : (
            <Button color='link' className='p-0 qr-target-btn' onClick={() => setShowForm(true)} title={t("Click to change card")}>
              {item.cardName || item.cardname || t('Default Card')}
            </Button>
          )}
        </div>

        {
        /* Action Buttons */
      }
        <div className="qr-card-actions">
          {isDefault ? (
            <Button color="outline-primary" size="sm" onClick={handleDownload} title={t("Download QR Code as PNG")} className="w-100 shadow-sm">
              <span role="img" aria-label="download">📥</span> {t("Download QR")}
            </Button>
          ) : (
            <>
              <Button color="outline-secondary" size="sm" onClick={() => setShowForm(true)} className="flex-fill">{t("Change Card")}</Button>
              <Button color="outline-primary" size="sm" onClick={handleDownload} title={t("Download QR Code as PNG")} className="flex-fill shadow-sm">
                <span role="img" aria-label="download">📥</span> {t("Download")}
              </Button>
              <Button color="outline-danger" size="sm" onClick={handleDelete} disabled={loading} className="flex-fill">{t("Delete")}</Button>
            </>
          )}
        </div>

        {error && <div className="text-danger mt-2 small text-center w-100">
            {error}
          </div>}
      </CardBody>
    </Card>;
};

const mapStateToProps = state => ({
  cards: state.auth.currentUser?.profile?.cards || []
});

const mapDispatchToProps = {
  setQRCard,
  deleteQRCode
};
export default connect(mapStateToProps, mapDispatchToProps)(QRCard);